import {
  alpha,
  darken,
  decomposeColor,
  getContrastRatio,
  lighten,
  recomposeColor,
  type Theme,
} from "@mui/material/styles";
import type { RevenueTrend } from "./manufacturerRevenueTrend";

export const MANUFACTURER_INSIGHTS_FEATURE_FLAG = "sqb.enable-mft-insights";
export const MANUFACTURER_DETAILS_DRAWER_WIDTH = "900px";

export type ManufacturerInsightsTimeRange = "last-12-months" | "calendar-year";
export const DEFAULT_MANUFACTURER_INSIGHTS_TIME_RANGE: ManufacturerInsightsTimeRange =
  "calendar-year";

// Placeholder until Product sets a real floor; today any spend at all earns a card.
export const MINIMUM_QUALIFYING_SPEND = 0;

export const CARD_LIFECYCLE_EVENT_LIMIT = 3;
export const CARD_SIGNAL_LIMIT = 5;
export const CARD_CAPABILITY_LIMIT = 4;

// Mirrors InsightsCmsFacade.MaxIdsPerRequest; anything larger is rejected as a 400.
export const MAX_MANUFACTURER_IDS_PER_REQUEST = 50;

export const LIFECYCLE_EVENT_COLOR = {
  Renewal: "primary.main",
  Refresh: "success.main",
} as const;

export type StatusTone = "success" | "warning" | "error";

// Stable has no tone, so it reads as secondary text.
export const REVENUE_TREND_TONE: Record<RevenueTrend, StatusTone | undefined> =
  {
    increasing: "success",
    decreasing: "warning",
    stable: undefined,
  };

const MIN_TEXT_CONTRAST = 4.5;
const TEXT_ADJUST_STEPS = 20;

function overlay(top: string, base: string): string {
  const [r, g, b, opacity = 1] = decomposeColor(top).values;
  const under = decomposeColor(base).values;
  const mix = (value: number, channel: number) =>
    Math.round(value * opacity + under[channel] * (1 - opacity));
  return recomposeColor({
    type: "rgb",
    values: [mix(r, 0), mix(g, 1), mix(b, 2)],
  });
}

// Drawer rows and card preview items are action.hover over background.default, the lowest-contrast text background.
export function getPanelRowBackground(theme: Theme): string {
  return overlay(theme.palette.action.hover, theme.palette.background.default);
}

// Darkens (light mode) or lightens (dark mode) a colour until small text in it reaches AA on the background.
export function getReadableTextColor(
  theme: Theme,
  color: string,
  background: string,
): string {
  const opaque = overlay(color, background);
  if (getContrastRatio(opaque, background) >= MIN_TEXT_CONTRAST) return color;
  const adjust = theme.palette.mode === "dark" ? lighten : darken;
  for (let step = 1; step < TEXT_ADJUST_STEPS; step++) {
    const candidate = adjust(opaque, step / TEXT_ADJUST_STEPS);
    if (getContrastRatio(candidate, background) >= MIN_TEXT_CONTRAST) {
      return candidate;
    }
  }
  return theme.palette.text.primary;
}

// Palette shades are tuned for fills, so start from the shade nearest the text colour and step to AA.
export function getStatusTextColor(theme: Theme, tone: StatusTone): string {
  const shade =
    theme.palette.mode === "dark"
      ? theme.palette[tone].light
      : theme.palette[tone].dark;
  return getReadableTextColor(theme, shade, getPanelRowBackground(theme));
}

export function getManufacturerRevenueTrendColors(
  theme: Theme,
  trend: RevenueTrend,
) {
  const tone = REVENUE_TREND_TONE[trend];
  const base = tone ? theme.palette[tone].main : theme.palette.text.secondary;
  const backgroundColor = alpha(
    base,
    theme.palette.mode === "dark" ? 0.18 : 0.1,
  );

  return {
    // The chip text sits on its own tint over the card, so it is checked against that blend.
    color: getReadableTextColor(
      theme,
      base,
      overlay(backgroundColor, theme.palette.background.paper),
    ),
    borderColor: base,
    backgroundColor,
  };
}
